import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
/**
 * Per-send stage tracing (#send-timeout-observability).
 *
 * `sendMessageViaWhatsapp` is guarded by a 50s Promise.race timeout; when it
 * fires, the persisted failure row says only "Timeout after 50000 ms" — zero
 * visibility into WHERE the time went (chat resolution? media prepare? the
 * poll dispatch? waiting for the sent message to appear in the store?).
 *
 * This module keeps a small bounded map of per-send traces, written by the
 * PAGE-context send path as it crosses stage boundaries. The content thunk
 * pulls a snapshot over the existing page-IPC bridge (`getSendTrace`) when the
 * timeout fires and folds the formatted trace into the durable messagelog row,
 * so a post-mortem can read "stuck at poll:sendAndWait for 50s, dispatched at
 * 3s, store-add never fired" straight out of Mongo.
 *
 * KEYING (review hardening): every ATTEMPT gets its own trace. A timed-out
 * attempt's page-side send keeps running after the guard fires; if a retry of
 * the same message starts meanwhile, both write marks concurrently. Keying by
 * userMessageId alone let the orphaned attempt corrupt (and terminate) the
 * retry's live trace — so `startSendTrace` returns an attempt-unique traceId
 * that all subsequent marks use, and `getSendTrace(userMessageId)` resolves
 * the LATEST attempt via a secondary index.
 *
 * Two kinds of entries:
 *  - STAGES: linear phases (`resolveChat`, `prepareChat`, `poll:sendAndWait`).
 *    Marking a new stage closes the previous one with its elapsed ms.
 *  - EVENTS: point-in-time annotations (prefixed `@`, ms = offset since trace
 *    start, e.g. `@poll:dispatched`) for parallel branches inside a stage —
 *    the poll path runs dispatch and store-add-wait concurrently, and knowing
 *    WHICH branch settled is exactly the signal that separates a slow
 *    dispatch from a hung store waiter.
 *
 * Pure module state — no window/DOM access — so it is unit-testable and safe
 * to import from any context (only the page context actually writes to it).
 */

export interface SendTraceEntry {
    /** Stage name, or `@`-prefixed point event. */
    stage: string;
    /** Stages: duration. Events: offset since trace start. Both in ms. */
    ms: number;
}

/** Media-send failure detail (#94). Written page-side by the media send path,
 *  read content-side off the snapshot so it can be stamped onto the durable
 *  messagelog. Page and content are separate module instances — this trace is
 *  the existing bridge between them, so no new IPC command is needed. */
export interface MediaFailInfo {
    /** Which non-OK return site produced the result (MediaFailSite). */
    site?: string;
    uploadStage?: string;
    uploadResultKind?: string;
    mimetype?: string;
    sizeBytes?: number;
    /** #371 stage/code/ack/result key names of the failure (sanitized upstream). */
    failStage?: string;
    failCode?: string;
    failAck?: number;
    resultKeys?: string;
    /** BE#1561 exception class name / redacted message behind a collapsed ERROR_UNKNOWN (sanitized upstream). */
    errorName?: string;
    errorMessage?: string;
    /** `describeChatForTrace` output for the recipient chat, on group sends. */
    chatDescriptor?: string;
}

export interface SendTraceSnapshot {
    userMessageId: string;
    chatId: string;
    type: string;
    /** Closed stages + point events, in order. */
    entries: SendTraceEntry[];
    /** Stage currently open (null once the trace ended). */
    currentStage: string | null;
    /** How long the current stage has been open (ms). */
    currentStageMs: number;
    /** Total ms since the trace started. */
    totalMs: number;
    /** Epoch ms the attempt started. */
    startedAt?: number;
    outcome?: 'ok' | 'error';
    error?: string;
    /** Present only on a media-send failure. */
    media?: MediaFailInfo;
}

interface SendTraceInternal {
    traceId: string;
    userMessageId: string;
    chatId: string;
    type: string;
    startedAt: number;
    entries: SendTraceEntry[];
    currentStage: string | null;
    currentStageStartedAt: number;
    outcome?: 'ok' | 'error';
    error?: string;
    endedAt?: number;
    media?: MediaFailInfo;
}

/** Bounded history — enough for any realistic in-flight overlap + a short tail
 *  of completed sends for late inspection, small enough to never matter. */
const MAX_TRACES = 25;
const traces = new Map<string, SendTraceInternal>();          // traceId → trace
const latestByMessage = new Map<string, string>();            // userMessageId → traceId
let seq = 0;

function evictIfNeeded(): void {
    if (traces.size <= MAX_TRACES) return;
    // #503: evict the oldest ENDED attempt first. A stuck attempt is exactly the one whose
    // eventual outcome the content side is still waiting to read (getSendTrace pinned by
    // startedAt); a busy campaign must not push it out before it settles.
    let oldest: string | undefined;
    for (const [id, t] of traces) {
        if (t.endedAt !== undefined) { oldest = id; break; }
    }
    if (oldest === undefined) oldest = traces.keys().next().value;
    if (oldest === undefined) return;
    const evicted = traces.get(oldest);
    traces.delete(oldest);
    if (evicted && latestByMessage.get(evicted.userMessageId) === oldest) {
        latestByMessage.delete(evicted.userMessageId);
    }
}

/**
 * #448: attempts still in flight, kept OUTSIDE the bounded history so the same-chat overlap
 * check still sees an orphaned (timed-out, still running) attempt after its trace was evicted.
 * An entry leaves on endSendTrace or after IN_FLIGHT_MAX_MS, so a hung send cannot suppress
 * attribution for its chat forever.
 */
const IN_FLIGHT_MAX_MS = 5 * 60 * 1000;
const inFlight = new Map<string, { chatId: string; startedAt: number }>();

/**
 * Begin a trace for one send ATTEMPT. Returns the attempt-unique traceId that
 * every subsequent mark/end call must use — an orphaned prior attempt keeps
 * writing to its own trace and can never touch a newer attempt's.
 */
export function startSendTrace(userMessageId: string, chatId: string, type: string): string {
    const traceId = `${userMessageId}#${++seq}`;
    const now = Date.now();
    traces.set(traceId, {
        traceId,
        userMessageId,
        chatId,
        type,
        startedAt: now,
        entries: [],
        currentStage: null,
        currentStageStartedAt: now,
    });
    if (userMessageId) latestByMessage.set(userMessageId, traceId);
    inFlight.set(traceId, { chatId, startedAt: now });
    evictIfNeeded();
    return traceId;
}

/** Call a boolean WhatsApp participants-collection method; `?` when it is absent or
 *  throws, so an unreadable answer is never reported as `false`. */
function askParticipants(participants: unknown, method: 'iAmMember' | 'iAmAdmin'): string {
    try {
        if (typeof participants !== 'object' || participants === null) return '?';
        const fn: unknown = Reflect.get(participants, method);
        if (typeof fn !== 'function') return '?';
        return String(fn.call(participants) === true);
    } catch {
        return '?';
    }
}

function boolOrUnknown(value: unknown): string {
    return typeof value === 'boolean' ? String(value) : '?';
}

/** Close the currently open stage (recording its duration) and open `stage`. */
/**
 * Compact, SPACE-FREE snapshot of a chat's characteristics, for embedding in the send
 * trace (formatSendTrace joins tokens on spaces, so the whole descriptor must be ONE token
 * — fields are comma-separated). `prepareChatForMessageSending` is an opaque WhatsApp store
 * call we cannot see inside; when it hangs, this is the only signal for WHICH chats hang.
 * Fully defensive — chat is `any` and a bad read must never break a send.
 */
export function describeChatForTrace(chat: any, chatId: string): string {
    try {
        const suffix = String(chatId).split('@')[1] || '?';
        const gm = chat?.groupMetadata;
        const participants =
            gm?.participants?.length ??
            gm?.participants?.getModelsArray?.().length ??
            (typeof gm?.size === 'number' ? gm.size : undefined);
        const msgs = chat?.msgs?.length ?? chat?.msgs?.getModelsArray?.().length ?? undefined;
        const parts = [
            `addr=${suffix}`,
            // No chat id at all (caller had a chat without id) → the engine flag is all there is.
            `group=${chatId ? chatKindOf(chatId) === 'group' : !!chat?.isGroup}`,
            `hasMeta=${!!gm}`,
            `participants=${participants ?? '?'}`,
            `msgs=${msgs ?? '?'}`,
            `unread=${chat?.unreadCount ?? '?'}`,
            `pendingLoad=${!!chat?.pendingInitialLoading}`,
            `recentLoaded=${chat?.hasLoadedRecentMsgs ?? '?'}`,
            `readOnly=${!!chat?.isReadOnly}`,
            // #445: the loaded participant list is partial for most groups, so its length
            // can't tell a dead membership from a live one. WhatsApp's own answer can:
            // participants.iAmMember()/iAmAdmin() resolve "me" via getMeParticipant
            // (incl. the alternate/LID wid); size is the group's real member count.
            `member=${askParticipants(gm?.participants, 'iAmMember')}`,
            `admin=${askParticipants(gm?.participants, 'iAmAdmin')}`,
            `size=${typeof gm?.size === 'number' ? gm.size : '?'}`,
            // The cached addressing mode / posting restrictions: a stale `lidMode` is what
            // WA's 421 nack (StaleGroupAddressingMode) is about.
            `lidMode=${boolOrUnknown(gm?.isLidAddressingMode)}`,
            `announce=${boolOrUnknown(gm?.announce)}`,
            `restrict=${boolOrUnknown(gm?.restrict)}`,
        ];
        return parts.join(',');
    } catch (e) {
        return `describe-failed:${String((e as Error)?.message ?? e).replace(/\s+/g, '_')}`;
    }
}

export function markSendStage(traceId: string, stage: string): void {
    const t = traces.get(traceId);
    if (!t || t.endedAt !== undefined) return;
    const now = Date.now();
    if (t.currentStage !== null) {
        t.entries.push({ stage: t.currentStage, ms: now - t.currentStageStartedAt });
    }
    t.currentStage = stage;
    t.currentStageStartedAt = now;
}

/** Record a point-in-time event (parallel-branch settlement) without touching
 *  the open stage. Rendered with an `@` prefix and an offset-since-start. */
export function markSendEvent(traceId: string, label: string): void {
    const t = traces.get(traceId);
    if (!t || t.endedAt !== undefined) return;
    t.entries.push({ stage: `@${label}`, ms: Date.now() - t.startedAt });
}

/** Merge media-send failure detail onto the trace (#94). Called from the media
 *  send path's non-OK return, and again to attach the chat descriptor. No-ops on
 *  an unknown or ended trace, matching markSendStage/markSendEvent. */
export function markSendMedia(traceId: string, info: MediaFailInfo): void {
    const t = traces.get(traceId);
    if (!t || t.endedAt !== undefined) return;
    t.media = { ...(t.media ?? {}), ...info };
}

/**
 * #503: the trace of a media send, keyed by its per-send MediaPrep instance (the
 * `this` of `sendToChat`, handed to `sendMediaMsgToChat` as its first argument), so
 * WhatsApp's send callback (media-prep-service) can mark into it. Kept off the
 * objects themselves so nothing new reaches WhatsApp's code.
 */
const mediaSendTraceIds = new WeakMap<object, string>();

export function bindMediaSendTrace(mediaPrep: object, traceId: string): void {
    mediaSendTraceIds.set(mediaPrep, traceId);
}

export function mediaSendTraceIdFor(mediaPrep: unknown): string | undefined {
    return typeof mediaPrep === 'object' && mediaPrep !== null ? mediaSendTraceIds.get(mediaPrep) : undefined;
}

export function endSendTrace(traceId: string, outcome: 'ok' | 'error', error?: string): void {
    const t = traces.get(traceId);
    inFlight.delete(traceId);
    if (!t || t.endedAt !== undefined) return;
    const now = Date.now();
    if (t.currentStage !== null) {
        t.entries.push({ stage: t.currentStage, ms: now - t.currentStageStartedAt });
        t.currentStage = null;
    }
    t.outcome = outcome;
    if (error) t.error = String(error).slice(0, 200);
    t.endedAt = now;
}

/**
 * #448: how many OTHER send attempts to the same chat overlapped the latest attempt of this
 * message in time (including an orphaned timed-out attempt). A send-job failure can be pinned
 * to a send by chat + time only when this is 0.
 */
export function overlappingSendsToSameChat(userMessageId: string): number {
    return countOverlappingSends(userMessageId, true);
}

/**
 * Same, across ALL chats. A server ack carries no chat, so it can be pinned to a send by time
 * only when nothing else was in flight anywhere during that send.
 */
export function overlappingSendsAnyChat(userMessageId: string): number {
    return countOverlappingSends(userMessageId, false);
}

function countOverlappingSends(userMessageId: string, sameChatOnly: boolean): number {
    const traceId = latestByMessage.get(userMessageId);
    const me = traceId !== undefined ? traces.get(traceId) : undefined;
    if (!me || traceId === undefined) return 0;
    const now = Date.now();
    const myEnd = me.endedAt ?? now;
    let n = 0;
    for (const [id, other] of inFlight) {
        if (id === traceId) continue;
        if (now - other.startedAt > IN_FLIGHT_MAX_MS) {
            inFlight.delete(id);
            continue;
        }
        // still in flight now, so it overlaps unless it began after this attempt ended
        if ((!sameChatOnly || other.chatId === me.chatId) && other.startedAt <= myEnd) n++;
    }
    for (const [id, t] of traces) {
        if (id === traceId || inFlight.has(id) || (sameChatOnly && t.chatId !== me.chatId)) continue;
        if (t.startedAt <= myEnd && (t.endedAt ?? now) >= me.startedAt) n++;
    }
    return n;
}

/**
 * Live snapshot of the LATEST attempt for a message — safe mid-flight.
 * #503: pass `startedAt` (from an earlier snapshot) to pin ONE attempt instead, so a
 * caller following a timed-out attempt to its eventual page-side outcome is not
 * handed a newer retry's trace. Null when that attempt is gone (evicted).
 */
export function getSendTrace(userMessageId: string, startedAt?: number): SendTraceSnapshot | null {
    let t: SendTraceInternal | undefined;
    if (startedAt !== undefined) {
        for (const candidate of traces.values()) {
            if (candidate.userMessageId === userMessageId && candidate.startedAt === startedAt) t = candidate;
        }
    } else {
        const traceId = latestByMessage.get(userMessageId);
        t = traceId !== undefined ? traces.get(traceId) : undefined;
    }
    if (!t) return null;
    const now = t.endedAt ?? Date.now();
    return {
        userMessageId: t.userMessageId,
        chatId: t.chatId,
        type: t.type,
        entries: [...t.entries],
        currentStage: t.currentStage,
        currentStageMs: t.currentStage !== null ? now - t.currentStageStartedAt : 0,
        totalMs: now - t.startedAt,
        startedAt: t.startedAt,
        outcome: t.outcome,
        error: t.error,
        media: t.media,
    };
}

/**
 * Compact single-line rendering for log rows, e.g.:
 *   `type=poll resolveChat=8 prepareChat=12 @poll:dispatched=3012 poll:sendAndWait=50001* total=50021`
 * A trailing `*` marks the still-open stage — the place the send is stuck.
 */
export function formatSendTrace(snap: SendTraceSnapshot | null): string {
    if (!snap) return 'no-trace';
    const parts: string[] = [`type=${snap.type}`];
    for (const e of snap.entries) parts.push(`${e.stage}=${e.ms}`);
    if (snap.currentStage !== null) parts.push(`${snap.currentStage}=${snap.currentStageMs}*`);
    parts.push(`total=${snap.totalMs}`);
    if (snap.outcome === 'error' && snap.error) parts.push(`err=${snap.error}`);
    return parts.join(' ');
}
